import { apiPost } from './client';

/* Sign-up checkout. Every endpoint, field name and fallback order below is
   taken from the host's Auth/Register.js so a plan bought from the new sign-up
   page goes through exactly the same backend path as one bought today.

   All of these are the PUBLIC billing routes (`/api/v1/public/...`) — they take
   a userId rather than a session, because the account exists but the user
   hasn't signed in yet. That is why they need no Authorization header. */

const RZP = '/api/v1/public/razorpay-billing';
const STRIPE = '/api/v1/public/billing';

/** Free plan. Indian users must go through the Razorpay endpoint even at zero
 *  cost, because that is what sets the org's paymentGateway to 'razorpay'. */
export const activateFreePlan = ({ userId, plan, couponCode, isIndian }) =>
  apiPost(`${isIndian ? RZP : STRIPE}/user-subscription-checkout`, {
    userId,
    plan,
    ...(couponCode ? { couponCode } : {}),
  });

/** `interval` picks the Razorpay billing term (monthly | quarterly | halfyearly
 *  | yearly) and buys a real recurring subscription on that term's plan. The
 *  legacy `months` prepay is still accepted by the server and stays available
 *  for older callers; the two are mutually exclusive server-side. */
export const razorpayOrgCheckout = ({ userId, plan, memberCount, months, interval, couponCode }) =>
  apiPost(`${RZP}/organization-subscription-checkout`, {
    userId,
    plan,
    memberCount,
    ...(interval ? { interval } : { months }),
    ...(couponCode ? { couponCode: couponCode.trim() } : {}),
  });

/** Same contract as razorpayOrgCheckout: `interval` selects the billing term and
 *  supersedes the legacy `months` prepay. Send one or the other, never both. */
export const stripeOrgCheckout = ({ userId, plan, memberCount, months, interval, couponCode }) =>
  apiPost(`${STRIPE}/organization-subscription-checkout`, {
    userId,
    plan,
    memberCount,
    ...(interval ? { interval } : { months }),
    ...(couponCode ? { couponCode: couponCode.trim() } : {}),
  });

export const verifyRazorpayPayment = (payload) => apiPost(`${RZP}/verify-payment`, payload);

/** Try the Razorpay coupon table first, then Stripe's — same order as the host.
 *  memberCount is total SEATS (team members + the admin). */
export const verifyCoupon = async ({ couponCode, memberCount }) => {
  const body = { couponCode: couponCode.trim(), memberCount };
  let res = null;
  try {
    res = await apiPost(`${RZP}/verify-coupon`, body);
  } catch {
    res = null;
  }
  if (!res?.data?.valid) {
    res = await apiPost(`${STRIPE}/verify-coupon`, body);
  }
  return res;
};

/* The backend answers GATEWAY_NOT_AVAILABLE / GATEWAY_DETECTION_FAILED when the
   customer simply isn't Indian. Any OTHER Razorpay error for an Indian customer
   must surface, never silently fall through to Stripe — that rule is load-bearing
   in the host and is reproduced in PlanSelection. */
export const isGatewayUnavailable = (err) => {
  const code = err?.data?.error;
  return code === 'GATEWAY_NOT_AVAILABLE' || code === 'GATEWAY_DETECTION_FAILED';
};
